import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import {
    IVA_TAX_TYPE,
    RECEPTOR_TAX_REGIMES,
    ReceptorTaxRegime,
    TAXES_PERCENTAGES_VALUES,
} from "@utils/Constants";

type LineTax = Pick<EDocumentProductDataType, "porcentaje_impuesto" | "codigo_tarifa">;

export const resolveReceptorTaxRegime = (key?: string | null): ReceptorTaxRegime | undefined =>
    key ? RECEPTOR_TAX_REGIMES[key] : undefined;

/**
 * Tarifa del producto en el catalogo. Ojo: en `Product` el campo `porcentaje_impuesto` guarda el
 * codigo de tarifa ("08"), mientras que en la linea guarda el porcentaje ("13").
 */
const productBaseTax = (line: EDocumentProductDataType): LineTax => {
    const productCode = line.product?.porcentaje_impuesto;
    if (productCode && TAXES_PERCENTAGES_VALUES[`${productCode}`] !== undefined) {
        return {
            porcentaje_impuesto: TAXES_PERCENTAGES_VALUES[`${productCode}`],
            codigo_tarifa: `${productCode}`,
        };
    }
    return { porcentaje_impuesto: line.porcentaje_impuesto, codigo_tarifa: line.codigo_tarifa };
};

/**
 * Tarifa que le toca a una linea segun la tarifa especial del receptor.
 *
 * La tarifa especial solo rebaja: un producto que ya grava menos (canasta basica al 1%) o que no
 * grava se queda con la suya. Sin regimen, la linea vuelve a la tarifa de su producto. Devuelve
 * `undefined` cuando la linea no se toca (exenta, otro impuesto, sin tarifa).
 */
export const resolveLineTaxForRegime = (
    line: EDocumentProductDataType,
    regimeKey?: string | null
): LineTax | undefined => {
    if (line.exento) return undefined;
    if (line.tipo_impuesto && `${line.tipo_impuesto}` !== IVA_TAX_TYPE) return undefined;

    const base = productBaseTax(line);
    const baseRate = Number(base.porcentaje_impuesto || 0);
    if (!baseRate) return undefined;

    const regime = resolveReceptorTaxRegime(regimeKey);
    if (regime && Number(regime.rate) < baseRate) {
        return { porcentaje_impuesto: regime.rate, codigo_tarifa: regime.codigoTarifa };
    }
    return base;
};

/**
 * Si la diferencia entre la tarifa facturada y la del CAByS la justifica la tarifa especial del
 * receptor: el CAByS grava mas que el regimen y la linea salio con la tarifa del regimen.
 */
export const isRateJustifiedByRegime = (
    catalogTaxRate: number,
    sentTaxRate: number,
    regimeKey?: string | null
): boolean => {
    const regime = resolveReceptorTaxRegime(regimeKey);
    if (!regime) return false;
    const regimeRate = Number(regime.rate);
    return catalogTaxRate > regimeRate && sentTaxRate === regimeRate;
};
